import {
  IssueTimelineDto,
  IssueTimelineDtoEventTypeEnum,
} from '@issuemanager/api-client';
import {
  ExtendedTypography,
  WrappedTypography,
} from '@issuemanager/apps/client/components';
import { urlValidator } from '@issuemanager/apps/client/utils';
import { Box, Stack } from '@mui/material';
import React from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { EditedActionTimelineEvents } from './edited-actions-timeline-events';
import {
  IssueTimelineAttachmentsEvents,
  StyledLink,
} from './issue-timeline-attachments-events';
import IssueTimelineChangesContainer from './issue-timeline-changes-container';
import { IssueTimelineEditedEvents } from './issue-timeline-edited-issue-events';
import { TimelineEventTypography } from './timeline-event-typography';

export interface IssueTimelineEventProps {
  data: IssueTimelineDto | IssueTimelineDto[];
}

export function IssueTimelineEvent(
  props: IssueTimelineEventProps,
): React.JSX.Element | null {
  const { data } = props;
  const { t } = useTranslation('common');

  if (Array.isArray(data)) {
    const { eventType } = data[0];

    if (eventType === IssueTimelineDtoEventTypeEnum.ActionEdited) {
      return <EditedActionTimelineEvents events={data} />;
    }

    return <IssueTimelineEditedEvents events={data} />;
  }

  const handleOpenRelationshipObject = (): void => {
    const url = data.bodyData.parameters.relationshipObjectUrl?.value ?? '';

    if (!url || !urlValidator(url)) {
      return;
    }

    const newWindow = window.open(url, '_blank', 'noopener,noreferrer');

    if (newWindow) {
      newWindow.opener = null;
    }
  };

  switch (data.eventType) {
    case IssueTimelineDtoEventTypeEnum.ActionAdded:
      return (
        <Stack data-testid='action-added-timeline-event'>
          <WrappedTypography>
            <Trans
              t={t}
              i18nKey='issueTimeline.actionAdded'
              values={{
                title: data.bodyData.parameters.actionTitle?.value,
              }}
            />
          </WrappedTypography>
          {data.bodyData.parameters.attachments && (
            <IssueTimelineChangesContainer pt={1}>
              <IssueTimelineAttachmentsEvents
                attachments={data.bodyData.parameters.attachments.value}
              />
            </IssueTimelineChangesContainer>
          )}
        </Stack>
      );
    case IssueTimelineDtoEventTypeEnum.ActionDeleted:
      return (
        <WrappedTypography data-testid='action-deleted-timeline-event'>
          <Trans
            t={t}
            i18nKey='issueTimeline.actionDeleted'
            values={{
              title: data.bodyData.parameters.actionTitle?.value,
            }}
          />
        </WrappedTypography>
      );
    case IssueTimelineDtoEventTypeEnum.IssueCreated:
      return (
        <>
          <WrappedTypography>
            <Trans
              t={t}
              i18nKey='issueTimeline.issueCreated'
              values={{
                code: data.bodyData.parameters.issueCode?.value,
                status: t('issueStatus.open'),
              }}
            />
          </WrappedTypography>
          {data.bodyData.parameters.attachments && (
            <IssueTimelineChangesContainer>
              <IssueTimelineAttachmentsEvents
                attachments={data.bodyData.parameters.attachments.value}
              />
            </IssueTimelineChangesContainer>
          )}
        </>
      );
    case IssueTimelineDtoEventTypeEnum.IssueAssigned:
      return (
        <WrappedTypography>
          <Trans
            t={t}
            i18nKey='issueTimeline.issueAssigned'
            values={{
              assignee: data.bodyData.parameters.issueOwnerName?.value,
              status: t('issueStatus.inProgress'),
            }}
          />
        </WrappedTypography>
      );
    case IssueTimelineDtoEventTypeEnum.IssueClosed:
      return (
        <WrappedTypography>
          <Trans
            t={t}
            i18nKey='issueTimeline.issueClosed'
            values={{
              code: data.bodyData.parameters.issueCode?.value,
              status: t('issueStatus.closed'),
            }}
          />
        </WrappedTypography>
      );
    case IssueTimelineDtoEventTypeEnum.IssueDiscarded:
      return (
        <Box>
          <WrappedTypography>
            <Trans
              t={t}
              i18nKey='issueTimeline.issueDiscarded'
              values={{
                code: data.bodyData.parameters.issueCode?.value,
              }}
            />
          </WrappedTypography>
          {data.bodyData.parameters.discardMessage?.value && (
            <WrappedTypography fontStyle='italic'>
              {data.bodyData.parameters.discardMessage.value}
            </WrappedTypography>
          )}
        </Box>
      );
    case IssueTimelineDtoEventTypeEnum.IssueApproved:
      return (
        <>
          <WrappedTypography>
            <Trans
              t={t}
              i18nKey='issueTimeline.issueApproved'
              values={{
                code: data.bodyData.parameters.issueCode?.value,
              }}
            />
          </WrappedTypography>
          {data.bodyData.parameters.attachments && (
            <IssueTimelineChangesContainer>
              <IssueTimelineAttachmentsEvents
                attachments={data.bodyData.parameters.attachments.value}
              />
            </IssueTimelineChangesContainer>
          )}
        </>
      );
    case IssueTimelineDtoEventTypeEnum.IssueDeclined:
      return (
        <WrappedTypography>
          <Trans
            t={t}
            i18nKey='issueTimeline.issueDeclined'
            values={{
              code: data.bodyData.parameters.issueCode?.value,
            }}
          />
        </WrappedTypography>
      );
    case IssueTimelineDtoEventTypeEnum.IssueToBeApproved:
      return (
        <WrappedTypography>
          <Trans
            t={t}
            i18nKey='issueTimeline.issueToBeApproved'
            values={{
              code: data.bodyData.parameters.issueCode?.value,
            }}
          />
        </WrappedTypography>
      );
    case IssueTimelineDtoEventTypeEnum.ActionCreated:
      return (
        <>
          <WrappedTypography>
            <Trans
              t={t}
              i18nKey='issueTimeline.actionCreated'
              values={{
                title: data.bodyData.parameters.actionTitle?.value,
              }}
            />
          </WrappedTypography>
          {data.bodyData.parameters.attachments && (
            <IssueTimelineChangesContainer>
              <IssueTimelineAttachmentsEvents
                attachments={data.bodyData.parameters.attachments.value}
              />
            </IssueTimelineChangesContainer>
          )}
        </>
      );
    case IssueTimelineDtoEventTypeEnum.ActionStarted:
      return (
        <WrappedTypography>
          <Trans
            t={t}
            i18nKey='issueTimeline.actionStarted'
            values={{
              title: data.bodyData.parameters.actionTitle?.value,
            }}
          />
        </WrappedTypography>
      );
    case IssueTimelineDtoEventTypeEnum.ActionInitiated:
      return (
        <WrappedTypography>
          <Trans
            t={t}
            i18nKey='issueTimeline.actionInitiated'
            values={{
              title: data.bodyData.parameters.actionTitle?.value,
            }}
          />
        </WrappedTypography>
      );
    case IssueTimelineDtoEventTypeEnum.ActionToBeApproved:
      return (
        <>
          <WrappedTypography>
            <Trans
              t={t}
              i18nKey='issueTimeline.actionToBeApproved'
              values={{
                title: data.bodyData.parameters.actionTitle?.value,
              }}
            />
          </WrappedTypography>
          {data.bodyData.parameters.attachments && (
            <IssueTimelineChangesContainer>
              <IssueTimelineAttachmentsEvents
                attachments={data.bodyData.parameters.attachments.value}
              />
            </IssueTimelineChangesContainer>
          )}
        </>
      );
    case IssueTimelineDtoEventTypeEnum.ActionApproved:
      return (
        <>
          <WrappedTypography>
            <Trans
              t={t}
              i18nKey='issueTimeline.actionApproved'
              values={{
                title: data.bodyData.parameters.actionTitle?.value,
              }}
            />
          </WrappedTypography>
          {data.bodyData.parameters.attachments && (
            <IssueTimelineChangesContainer>
              <IssueTimelineAttachmentsEvents
                attachments={data.bodyData.parameters.attachments.value}
              />
            </IssueTimelineChangesContainer>
          )}
        </>
      );
    case IssueTimelineDtoEventTypeEnum.ActionRejected:
      return (
        <>
          <WrappedTypography>
            <Trans
              t={t}
              i18nKey='issueTimeline.actionRejected'
              values={{
                title: data.bodyData.parameters.actionTitle?.value,
              }}
            />
          </WrappedTypography>
          <IssueTimelineChangesContainer>
            <TimelineEventTypography>
              <Trans
                t={t}
                i18nKey='issueTimeline.actionRejectedDescription'
                values={{
                  description: data.bodyData.parameters.reason?.value,
                }}
              />
            </TimelineEventTypography>
            {data.bodyData.parameters.attachments && (
              <IssueTimelineAttachmentsEvents
                attachments={data.bodyData.parameters.attachments.value}
              />
            )}
          </IssueTimelineChangesContainer>
        </>
      );
    case IssueTimelineDtoEventTypeEnum.ActionDiscarded:
      return (
        <WrappedTypography>
          <Trans
            t={t}
            i18nKey='issueTimeline.actionDiscarded'
            values={{
              title: data.bodyData.parameters.actionTitle?.value,
            }}
          />
        </WrappedTypography>
      );
    case IssueTimelineDtoEventTypeEnum.ActionCompleted:
      return (
        <>
          <WrappedTypography>
            <Trans
              t={t}
              i18nKey='issueTimeline.actionCompleted'
              values={{
                title: data.bodyData.parameters.actionTitle?.value,
              }}
            />
          </WrappedTypography>
          {data.bodyData.parameters.attachments && (
            <IssueTimelineChangesContainer>
              <IssueTimelineAttachmentsEvents
                attachments={data.bodyData.parameters.attachments.value}
              />
            </IssueTimelineChangesContainer>
          )}
        </>
      );
    case IssueTimelineDtoEventTypeEnum.ActionOpened:
      return (
        <WrappedTypography>
          <Trans
            t={t}
            i18nKey='issueTimeline.actionOpened'
            values={{
              title: data.bodyData.parameters.actionTitle?.value,
            }}
          />
        </WrappedTypography>
      );
    case IssueTimelineDtoEventTypeEnum.CommentAdded:
      return (
        <>
          <WrappedTypography>
            <Trans
              t={t}
              i18nKey='issueTimeline.commentAdded'
              values={{
                comment: data.bodyData.parameters.issueComment?.value,
              }}
            />
          </WrappedTypography>
          {data.bodyData.parameters.attachments && (
            <IssueTimelineChangesContainer>
              <IssueTimelineAttachmentsEvents
                attachments={data.bodyData.parameters.attachments.value}
              />
            </IssueTimelineChangesContainer>
          )}
        </>
      );
    case IssueTimelineDtoEventTypeEnum.AttachmentDeleted:
      return (
        <>
          <WrappedTypography>
            <Trans
              t={t}
              i18nKey={
                data.bodyData.parameters.actionTitle
                  ? 'issueTimeline.actionEdited'
                  : 'issueTimeline.issueEdited'
              }
              values={{
                title: data.bodyData.parameters.actionTitle?.value,
              }}
            />
          </WrappedTypography>
          {data.bodyData.parameters.attachments && (
            <IssueTimelineChangesContainer>
              <IssueTimelineAttachmentsEvents
                attachments={data.bodyData.parameters.attachments.value.map(
                  (attachment) => ({
                    ...attachment,
                    deleted: true,
                  }),
                )}
              />
            </IssueTimelineChangesContainer>
          )}
        </>
      );
    case IssueTimelineDtoEventTypeEnum.RelationshipAdded:
      return (
        <WrappedTypography>
          <Trans
            t={t}
            i18nKey='issueTimeline.relationshipAdded'
            values={{ type: data.bodyData.parameters.relationshipType?.value }}
          />
          <IssueTimelineChangesContainer>
            <ExtendedTypography>
              <StyledLink onClick={handleOpenRelationshipObject}>
                {data.bodyData.parameters.relationshipObjectId?.value ?? ''}
              </StyledLink>
              {data.bodyData.parameters.relationshipObjectId?.value &&
              data.bodyData.parameters.relationshipObjectName?.value
                ? ' - '
                : ''}
              {data.bodyData.parameters.relationshipObjectName?.value ?? ''}
            </ExtendedTypography>
          </IssueTimelineChangesContainer>
        </WrappedTypography>
      );
    case IssueTimelineDtoEventTypeEnum.RelationshipDeleted:
      return (
        <WrappedTypography>
          <Trans
            t={t}
            i18nKey='issueTimeline.relationshipDeleted'
            values={{ type: data.bodyData.parameters.relationshipType?.value }}
          />
          <IssueTimelineChangesContainer>
            <ExtendedTypography>
              <StyledLink onClick={handleOpenRelationshipObject}>
                {data.bodyData.parameters.relationshipObjectId?.value ?? ''}
              </StyledLink>
              {data.bodyData.parameters.relationshipObjectId?.value &&
              data.bodyData.parameters.relationshipObjectName?.value
                ? ' - '
                : ''}
              {data.bodyData.parameters.relationshipObjectName?.value ?? ''}
            </ExtendedTypography>
          </IssueTimelineChangesContainer>
        </WrappedTypography>
      );
    default:
      return null;
  }
}
